import React from "react";
import { DatePicker, Form, Input, InputNumber, Select, Switch, Tag } from "antd";
import dayjs from "dayjs";
import type { FieldDef } from "@evo-flywheel/contracts";
import { DeadlineTag, MarkdownInput, deadlineOf, isMarkdown, valueLabel } from "./modules/hints";
import { stripRegister } from "./modules/aml-register";
import { RefCell, RefSelect } from "./modules/refs";
import { picklists } from "@kit/picklists";

/** One cell. `slug` enables the descriptor hints: deadline urgency, Markdown (shown as plain excerpt). */
export function renderCell(field: FieldDef | undefined, value: unknown, slug?: string): React.ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" && value.includes("<!-- AML_REGISTER:")) value = stripRegister(value) || "—";
  const deadline = field && deadlineOf(slug, field.name);
  if (deadline) return <span>{dayjs(String(value)).format("YYYY-MM-DD")} <DeadlineTag value={value} warnDays={deadline.warnDays} /></span>;
  if (field && isMarkdown(slug, field.name)) {
    const plain = String(value).replace(/[#*_`>[\]()-]/g, "").replace(/\s+/g, " ").trim();
    return plain.length > 120 ? `${plain.slice(0, 120)}…` : plain;
  }
  if (field?.type === "ref" && (typeof value === "string" || typeof value === "number")) return <RefCell id={String(value)} refTo={field.refTo} />;
  if (field?.type === "boolean") {
    const on = value === true || value === "1" || value === 1;
    return <Tag color={on ? "green" : "default"}>{on ? "Sì" : "No"}</Tag>;
  }
  if (field && /status|stato|esito|criticality|risk|level/i.test(field.name)) return <StatusTag value={String(value)} />;
  if (field?.type === "date") return dayjs(String(value)).format("YYYY-MM-DD");
  if (field?.type === "datetime") return dayjs(String(value)).format("YYYY-MM-DD HH:mm");
  if (field?.type === "json") return <code>{JSON.stringify(value)}</code>;
  return String(value);
}

function control(field: FieldDef, slug?: string): React.ReactNode {
  if (isMarkdown(slug, field.name)) return <MarkdownInput />;
  // coreBOS user/reference fields are pickers, picklists are selects (UI contract rule 15)
  if (field.type === "ref") return <RefSelect refTo={field.refTo} />;
  const values = picklists[slug ?? ""]?.[field.name];
  if (values?.length) return <Select allowClear showSearch options={values.map((v) => ({ value: v, label: valueLabel(v) }))} />;
  if (field.type === "boolean") return <Switch />;
  if (field.type === "number" || field.type === "decimal") return <InputNumber style={{ width: "100%" }} />;
  if (field.type === "date") return <DatePicker style={{ width: "100%" }} />;
  if (field.type === "datetime") return <DatePicker showTime style={{ width: "100%" }} />;
  if (field.type === "json" || field.name === "description" || field.name === "content") return <Input.TextArea rows={4} />;
  return <Input />;
}

export function FormField({ field, required, initialValue, slug }: { field: FieldDef; required?: boolean; initialValue?: string; slug?: string }): React.ReactElement {
  const isDate = field.type === "date" || field.type === "datetime";
  const format = field.type === "datetime" ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD";
  return <Form.Item name={field.name} label={field.label ?? field.name} initialValue={initialValue}
    valuePropName={field.type === "boolean" ? "checked" : undefined}
    rules={required ? [{ required: true, message: `${field.label ?? field.name}: campo obbligatorio` }] : undefined}
    {...(isDate ? { getValueProps: (value: unknown) => ({ value: value ? dayjs(String(value)) : undefined }), getValueFromEvent: (date: dayjs.Dayjs | null) => date ? date.format(format) : null } : {})}>
    {control(field, slug)}
  </Form.Item>;
}

/** Fleet status palette (ADR-013 §2.4): meaning, not module tone. */
const STATUS: Array<[RegExp, string]> = [
  [/^(active|attiv|implemented|implementat|completed|completat|assessed|approved|accettat|signed|low|basso)/i, "green"],
  [/^(in_progress|in corso|pending|planned|draft|bozza|da valutare|tbd|medium|medio|moderat)/i, "gold"],
  [/^(failed|critical|critic|high|alto|rejected|rifiutat|catastrof|significativ)/i, "red"],
  [/^(archived|archiviat|not_required|inactive|standard)/i, "default"],
];
export const StatusTag: React.FC<{ value: string }> = ({ value }) =>
  <Tag color={STATUS.find(([re]) => re.test(value))?.[1] ?? "blue"}>{valueLabel(value)}</Tag>;
